import React, { useState, useMemo, useRef, useEffect, useContext } from "react";
import "../../../assets/css/superadmin/topuprequest/TopUpRequest.css";
import MasterLayout from "../../../layout/superadmin/MasterLayout";
import axios from "axios";
import AppContext from "../../../context/AppContext";
/* ---------------------------------------------------------
   Base URL — apni actual API baseURL yahan daal dena
   --------------------------------------------------------- */

const OUTER_TABS = [
  { key: "all", label: "All requests" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

function initials(name) {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function StatusBadge({ status }) {
  const text = status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className={`tr-badge tr-badge-${status}`}>
      <span className="tr-badge-dot" />
      {text}
    </span>
  );
}

/* ---------- Row action dropdown (3-dot menu) ---------- */
function RowActionsDropdown({ request, onView, onAccept, onReject }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isPending = request.status === "pending";

  return (
    <div className="tr-dropdown-wrapper" ref={wrapRef}>
      <button
        className="tr-dots-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Row actions"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="12" cy="19" r="1.8" />
        </svg>
      </button>

      {open && (
        <div className="tr-custom-dropdown-menu">
          <button
            className="tr-custom-dropdown-item tr-item-view"
            onClick={() => {
              setOpen(false);
              onView(request);
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            View details
          </button>

          {isPending && (
            <>
              <div className="tr-custom-dropdown-divider" />
              <button
                className="tr-custom-dropdown-item tr-item-accept"
                onClick={() => {
                  setOpen(false);
                  onAccept(request);
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Approve
              </button>
              <button
                className="tr-custom-dropdown-item tr-item-reject"
                onClick={() => {
                  setOpen(false);
                  onReject(request);
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                Reject
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function TopupRequests() {
  const { baseUrl, baseUrlForFile ,authToken } = useContext(AppContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [detailModal, setDetailModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { type: 'approve'|'reject', request }
  const [rejectReason, setRejectReason] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const allRequests = requests;

  const pendingRequests = requests.filter((item) => item.status === "pending");

  const approvedRequests = requests.filter(
    (item) => item.status === "approved",
  );

  const rejectedRequests = requests.filter(
    (item) => item.status === "rejected",
  );

  /* ---------------------------------------------------------
     Fetch top-up requests (GET /admin/topup-requests)
     --------------------------------------------------------- */
  const fetchTopupRequests = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/superadmin/topup/fetchAllTopups`, {
        headers: { "Content-Type": "application/json" ,
          Authorization: `Bearer ${authToken}`
        },
      })
      .then((res) => {
        if (res.data.success) {
          console.log("Fetched top-up requests:", res.data.topuprequests);
          setRequests(res.data.topuprequests);
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false)); // ← ye ek line add karni hai
  };

  useEffect(() => {
    fetchTopupRequests();
  }, []);

  const counts = useMemo(() => {
    return {
      all: (requests || []).length,
      pending: (requests || []).filter((r) => r.status === "pending").length,
      approved: (requests || []).filter((r) => r.status === "approved").length,
      rejected: (requests || []).filter((r) => r.status === "rejected").length,
    };
  }, [requests]);

  const filtered = useMemo(() => {
    return requests.filter((r) => {
      const matchesTab = activeTab === "all" || r.status === activeTab;
      const matchesStatus = statusFilter === "all" || r.status === statusFilter;
      const matchesSearch =
        searchTerm.trim() === "" ||
        (r.mechanicadmin?.fullname || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (r.mechanicadmin?.email || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        String(r.id).toLowerCase().includes(searchTerm.toLowerCase());
      return matchesTab && matchesStatus && matchesSearch;
    });
  }, [requests, activeTab, statusFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  function openConfirm(type, request) {
    setRejectReason("");
    setConfirmModal({ type, request });
  }

  function closeConfirm() {
    setConfirmModal(null);
    setRejectReason("");
  }

  /* ---------------------------------------------------------
     Approve / Reject request (POST /admin/topup-requests/:id/status)
     --------------------------------------------------------- */
  function handleConfirm() {
  if (!confirmModal) return;
  const { type, request } = confirmModal;
  const newStatus = type === "approve" ? "approved" : "rejected";

  axios
    .post(
      `${baseUrl}/superadmin/topup/updateStatus`,
      {
        id: request.id,
        status: newStatus,
      },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    )
    .then((res) => {
      fetchTopupRequests();
      closeConfirm();
    })
    .catch((err) => console.log(err));
}

  return (
    <MasterLayout>
      <div className="tr-page">
        {/* Header */}
        <div className="tr-header">
          <h1>Top-up requests</h1>
          <p>
            Review wallet top-up requests submitted by mechanics, verify payment
            proof, and approve or reject each one.
          </p>
        </div>

        {/* Stat cards */}
        <div className="tr-stats">
          <div className="tr-stat-card">
            <div className="tr-stat-icon">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 3" />
              </svg>
            </div>
            <div className="tr-stat-info">
              <p className="tr-stat-value">{counts.pending}</p>
              <p className="tr-stat-label">Pending</p>
            </div>
          </div>

          <div className="tr-stat-card">
            <div className="tr-stat-icon">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div className="tr-stat-info">
              <p className="tr-stat-value">{counts.approved}</p>
              <p className="tr-stat-label">Approved</p>
            </div>
          </div>

          <div className="tr-stat-card">
            <div className="tr-stat-icon">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <div className="tr-stat-info">
              <p className="tr-stat-value">{counts.rejected}</p>
              <p className="tr-stat-label">Rejected</p>
            </div>
          </div>

          <div className="tr-stat-card">
            <div className="tr-stat-icon">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="2" y="6" width="20" height="13" rx="2" />
                <path d="M2 10h20" />
              </svg>
            </div>
            <div className="tr-stat-info">
              <p className="tr-stat-value">{counts.all}</p>
              <p className="tr-stat-label">Total requests</p>
            </div>
          </div>
        </div>

        {/* Outer tabs */}
        <div className="tr-outer-tabs-wrap">
          <div className="tr-outer-tabs">
            {OUTER_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`tr-outer-tab ${activeTab === tab.key ? "tr-outer-tab-active" : ""}`}
                onClick={() => {
                  setActiveTab(tab.key);
                  setPage(1);
                }}
              >
                {tab.label}
                <span className="tr-outer-tab-count">({counts[tab.key]})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Table card */}
        <div className="tr-table-card">
          <div className="tr-table-heading">
            <h2 className="tr-table-title">Requests</h2>
          </div>

          <div className="tr-table-wrapper">
            <table className="tr-table">
              <thead>
                <tr>
                  <th className="tr-col-mechanic">Mechanic</th>
                  <th className="tr-col-amount">Amount</th>
                  <th className="tr-col-date">Date</th>
                  <th className="tr-col-status">Status</th>
                  <th className="tr-col-action">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="tr-empty-row">
                      Loading requests...
                    </td>
                  </tr>
                ) : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="tr-empty-row">
                      No top-up requests match your filters.
                    </td>
                  </tr>
                ) : (
                  paginated.map((r) => (
                    <tr key={r.id}>
                      <td className="tr-col-mechanic">
                        <div className="tr-mechanic-cell">
                          <img
                            src={`${baseUrlForFile}/${r.mechanicadmin?.image}`}
                            alt=""
                            className="bm-avatar-img"
                          />
                          <div className="tr-mechanic-info">
                            <div className="tr-mechanic-name">
                              {r.mechanicadmin?.fullname || "Unknown"}
                            </div>
                            <div className="tr-mechanic-meta">
                              {r.mechanicadmin?.email || "N/A"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="tr-col-amount">
                        <span className="tr-amount">
                          PKR {Number(r.amount).toLocaleString()}
                        </span>
                      </td>
                      <td className="tr-col-date">
                        <span className="tr-date-col">
                          {r.created_at ? r.created_at.split("T")[0] : "N/A"}
                        </span>
                      </td>
                      <td className="tr-col-status">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="tr-col-action tr-text-center">
                        <RowActionsDropdown
                          request={r}
                          onView={setDetailModal}
                          onAccept={(req) => openConfirm("approve", req)}
                          onReject={(req) => openConfirm("reject", req)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="tr-pagination-wrap">
            <span className="tr-pagination-info">
              Showing {paginated.length === 0 ? 0 : (page - 1) * pageSize + 1}–
              {Math.min(page * pageSize, filtered.length)} of {filtered.length}
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className="tr-page-btn"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`tr-page-btn ${p === page ? "active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className="tr-page-btn"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* ===== Detail Modal (proof + info) ===== */}
        {detailModal && (
          <div
            className="tr-detail-modal"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: 16,
            }}
            onClick={() => setDetailModal(null)}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="tr-modal-header">
                  <div className="tr-modal-header-left">
                    <h5>{detailModal.id}</h5>
                    <span>Top-up request details</span>
                  </div>
                  <button
                    className="tr-modal-close"
                    onClick={() => setDetailModal(null)}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>

                <div className="tr-modal-body">
                  <div className="tr-modal-status-bar">
                    <div className="sb-left">
                      <span className="sb-id">{detailModal.id}</span>
                    </div>
                    <StatusBadge status={detailModal.status} />
                  </div>

                  <h6 className="tr-section-title">Mechanic</h6>
                  <div className="tr-modal-mechanic-strip">
                    <div className="ms-avatar">
                      <img
                        src={`${baseUrlForFile}/${detailModal.mechanicadmin?.image}`}
                        alt="Mechanic"
                        className="mrm-avatar-img"
                      />
                    </div>
                    <div>
                      <div className="ms-name">
                        {detailModal.mechanicadmin?.fullname || "Unknown"}
                      </div>
                      <div className="ms-meta">
                        {detailModal.mechanicadmin?.email || "N/A"}
                      </div>
                    </div>
                  </div>

                  <h6 className="tr-section-title">Request info</h6>
                  <div className="tr-info-grid">
                    <div className="tr-info-item">
                      <label>Amount requested</label>
                      <span>
                        PKR {Number(detailModal.amount).toLocaleString()}
                      </span>
                    </div>
                    <div className="tr-info-item">
                      <label>Submitted on</label>
                      <span>
                        {detailModal.created_at
                          ? detailModal.created_at.split("T")[0]
                          : "N/A"}
                      </span>
                    </div>
                  </div>

                  <h6 className="tr-section-title">Payment proof</h6>
                  <div className="tr-proof-block">
                    <div className="tr-proof-image-wrap">
                      <img
                        src={`${baseUrlForFile}/${detailModal.paymentproof}`}
                        alt="Full payment proof"
                      />
                    </div>
                  </div>

                  {detailModal.status === "rejected" &&
                    detailModal.rejectReason && (
                      <div className="tr-reason-box">
                        <label>Rejection reason</label>
                        <span>{detailModal.rejectReason}</span>
                      </div>
                    )}
                </div>

                {detailModal.status === "pending" && (
                  <div className="tr-modal-footer-list">
                    <button
                      className="tr-mf-btn tr-mf-accept"
                      onClick={() => {
                        setDetailModal(null);
                        openConfirm("approve", detailModal);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Approve this request
                    </button>
                    <button
                      className="tr-mf-btn tr-mf-reject"
                      onClick={() => {
                        setDetailModal(null);
                        openConfirm("reject", detailModal);
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      Reject this request
                    </button>
                    <button
                      className="tr-mf-btn tr-mf-close"
                      onClick={() => setDetailModal(null)}
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== Confirm Modal (approve / reject) ===== */}
        {confirmModal && (
          <div
            className="tr-confirm-modal"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: 16,
            }}
            onClick={closeConfirm}
          >
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="tr-confirm-body">
                  <div
                    className={`tr-confirm-icon ${confirmModal.type === "approve" ? "approve" : "reject"}`}
                  >
                    {confirmModal.type === "approve" ? (
                      <svg
                        width="26"
                        height="26"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg
                        width="26"
                        height="26"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    )}
                  </div>

                  <p className="tr-confirm-title">
                    {confirmModal.type === "approve"
                      ? "Approve this top-up?"
                      : "Reject this top-up?"}
                  </p>
                  <p className="tr-confirm-text">
                    {confirmModal.request.mechanicadmin?.fullname ||
                      "This mechanic"}{" "}
                    requested{" "}
                    <strong>
                      PKR {Number(confirmModal.request.amount).toLocaleString()}
                    </strong>
                    .{" "}
                    {confirmModal.type === "approve"
                      ? "This will be added to their wallet balance immediately."
                      : "Their wallet balance will not be affected."}
                  </p>

                  {confirmModal.type === "reject" && (
                    <div style={{ marginBottom: 4 }}>
                      <label
                        style={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "var(--text-muted)",
                          display: "block",
                          marginBottom: 6,
                        }}
                      >
                        Reason{" "}
                        <span className="tr-optional-tag">(optional)</span>
                      </label>
                      <textarea
                        className="tr-reason-textarea"
                        placeholder="e.g. Proof image unreadable, amount mismatch..."
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                <div className="tr-confirm-footer">
                  <button
                    className="tr-confirm-btn cancel"
                    onClick={closeConfirm}
                  >
                    Cancel
                  </button>
                  <button
                    className={`tr-confirm-btn ${confirmModal.type === "approve" ? "approve" : "reject"}`}
                    onClick={handleConfirm}
                  >
                    {confirmModal.type === "approve"
                      ? "Yes, approve"
                      : "Yes, reject"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MasterLayout>
  );
}
